/** @jsxImportSource @emotion/react */
import {
  getColorBackgroundSelector,
  getColorGreySelector,
  getColorPrimarySelector,
  getColorTextSelector,
  getSpacingSelector
} from '@signal/theme/selectors'
import Typography from '@signal/components/Typography'
import { darken, lighten, Theme, TypographyProps } from '@material-ui/core'
import estyled, { ecss } from '@signal/theme/emotion'

export const TabsContainer = estyled.nav`
  display: flex;
  align-items: center;
  height: 100%;
  max-width: calc(100% - 50px);
  overflow-x: auto;
  overflow-y: hidden;

  /* Hide scrollbar */
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera */
  }

  flex: 1;
  justify-content: flex-start;
  gap: ${getSpacingSelector(1)}px;
  padding: 0 ${getSpacingSelector(2)}px;
  background-color: ${props => getColorBackgroundSelector('paper')(props)};
`

interface TabItemProps {
  $isActive: boolean
}

export const TabItem = estyled.div<TabItemProps>`
  position: relative;
  height: 100%;
  display: flex;
  align-items: center;
`

interface TabLinkProps {
  $isActive: boolean
  $isDarkMode: boolean
}

export const TabText = estyled<TabLinkProps & TypographyProps>(Typography)`
  color: ${props =>
    props.$isActive
      ? getColorPrimarySelector()(props)
      : getColorTextSelector()(props)} !important;

  font-weight: ${props => (props.$isActive ? 600 : 400)} !important;
  transition:
    color 0.2s ease,
    scale 0.2s ease;
`

export const TabIndicator = estyled.div<TabLinkProps>`
  position: absolute;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  height: 6px;
  width: 40px;
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  border: 0.2px solid
    ${props => {
      if (props.$isDarkMode) {
        return props.$isActive
          ? darken(getColorPrimarySelector()(props.theme), 0.3)
          : getColorGreySelector(700)(props)
      }
      return props.$isActive
        ? lighten(getColorPrimarySelector()(props.theme), 0.7)
        : getColorGreySelector(200)(props)
    }};
  background-color: ${props => {
    if (props.$isDarkMode) {
      return props.$isActive
        ? darken(getColorPrimarySelector()(props.theme), 0.5)
        : 'transparent'
    }
    return props.$isActive
      ? lighten(getColorPrimarySelector()(props.theme), 0.9)
      : 'transparent'
  }} !important;
  border-bottom: none;

  transition:
    border-color 0.2s ease,
    background-color 0.2s ease;
`
export const TabLink = estyled.a<TabLinkProps>`
  display: flex;
  align-items: center;
  height: 100%;
  padding: 0 ${getSpacingSelector(2)}px;
  text-decoration: none !important;
  font-size: 14px;
  font-weight: 500;

  transition: color 0.2s ease;
  cursor: pointer;
  white-space: nowrap;
  &:hover {
    // @ts-ignore
    .tabText {
      color: ${getColorPrimarySelector()} !important;
      scale: 1.02;
    }

    .TabIndicator {
      ${({ $isActive, $isDarkMode, theme }: TabLinkProps & { theme: Theme }) =>
        !$isActive &&
        ecss`
          border-color: ${
            $isDarkMode
              ? darken(getColorPrimarySelector()(theme), 0.5)
              : lighten(getColorPrimarySelector()(theme), 0.9)
          } !important;
          background-color: ${
            $isDarkMode
              ? darken(getColorPrimarySelector()(theme), 0.5)
              : lighten(getColorPrimarySelector()(theme), 0.9)
          } !important;
        `}
    }
  }

  &:focus {
    outline: none;
    color: ${getColorPrimarySelector()};
  }
`

export const TabsScrollContainer = estyled.div`
  position: relative;
  display: flex;
  align-items: center;
  height: 100%;
  flex: 1;

  width: calc(100% - 450px);
`

interface ScrollButtonProps {
  $visible: boolean
  $direction: 'left' | 'right'
}

export const ScrollButton = estyled.button<ScrollButtonProps>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => (props.$direction === 'left' ? 'left: 8px;' : 'right: 8px;')}

  width: 32px;
  height: 32px;
  background: transparent;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  z-index: 20; /* Higher z-index to stay above blur overlay */

  display: ${props => (props.$visible ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;

  transition:
    opacity 0.2s ease,
    background-color 0.2s ease;

  &:active {
    transform: translateY(-50%) scale(0.95);
  }

  svg {
    width: 25px;
    height: 25px;
    color: ${props => getColorGreySelector(600)(props)};

    &:hover {
      color: ${props => getColorPrimarySelector()(props)};
    }
  }
`

interface BlurOverlayProps {
  $visible: boolean
  $direction: 'left' | 'right'
}

export const BlurOverlay = estyled.div<BlurOverlayProps>`
  position: absolute;
  top: 0;
  ${props => (props.$direction === 'left' ? 'left: 0;' : 'right: 0;')}

  width: 48px;
  height: 100%;
  z-index: 10;
  pointer-events: none; /* Don't interfere with interactions */

  display: ${props => (props.$visible ? 'block' : 'none')};

  background: ${props => {
    const direction = props.$direction === 'left' ? 'to right' : 'to left'
    const paperColor = getColorBackgroundSelector('paper')(props)
    return `linear-gradient(${direction}, 
      ${paperColor}ee 0%, 
      ${paperColor}cc 30%, 
      ${paperColor}88 60%, 
      ${paperColor}44 80%, 
      transparent 100%
    )`
  }};

  backdrop-filter: blur(2px);
  -webkit-backdrop-filter: blur(2px);

  transition: opacity 0.2s ease;
`
